'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { THEME_COOKIE, THEME_COOKIE_MAX_AGE, type ResolvedTheme, type ThemePreference } from './theme';

type ThemeContextValue = {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  resolved: 'dark',
  setPreference: () => undefined,
});

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyAttribute(theme: ResolvedTheme) {
  document.querySelectorAll<HTMLElement>('[data-product-root]').forEach((el) => {
    el.dataset.theme = theme;
  });
}

/**
 * Owns the theme preference on the client. The first render uses the value the
 * server read from the cookie (so markup matches); `system` is reconciled with
 * `prefers-color-scheme` after mount and kept in sync with OS changes.
 */
export function ThemeProvider({ initialPreference, children }: { initialPreference: ThemePreference; children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(initialPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(initialPreference === 'system' ? 'dark' : initialPreference);

  useEffect(() => {
    if (preference !== 'system') {
      setResolved(preference);

      return;
    }

    const mq = window.matchMedia('(prefers-color-scheme: dark)');

    const sync = () => {
      const next = mq.matches ? 'dark' : 'light';
      setResolved(next);
      applyAttribute(next);
    };

    sync();
    mq.addEventListener('change', sync);

    return () => mq.removeEventListener('change', sync);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    document.cookie = `${THEME_COOKIE}=${next}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
    applyAttribute(next === 'system' ? systemTheme() : next);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
